import moment from "moment";
import { sampleData } from "./sampleData.js";
import { sampleConfig } from "./sampleConfig.js";

export { sampleData, sampleConfig };

/* ===== HELPERS ===== */
export const dnum = (s) => Math.round(Date.parse(s + "T12:00:00Z") / 864e5);
export const fmt$ = (n) =>
  (n < 0 ? "−$" : "$") + Math.abs(Math.round(n || 0)).toLocaleString("en-US");
export const pct = (v, d = 0) => ((v || 0) * 100).toFixed(d) + "%";
export const r2 = (n) => Math.round((n || 0) * 100) / 100;
export const r6 = (n) => Math.round((n || 0) * 1e6) / 1e6;
export const isNum = (v) => typeof v === "number" && isFinite(v);
export const pctTxt = (v) => (isNum(v) ? Math.round(v * 10000) / 100 + "%" : "—");
export const dObj = (s) => new Date(s + "T12:00:00");
export const md = (s) =>
  dObj(s).toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const dmd = (s) =>
  dObj(s).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
export const longD = (s) =>
  dObj(s).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
export const vcls = (v) => "v-" + String(v || "").replace(/[^A-Za-z]/g, "");

export const promoNet = (p, bar) =>
  bar * (1 - p.disc) * (1 - p.comm - p.fee) - p.pkg;
export const combNet = (p, bar, sf) =>
  bar * (1 - p.disc) * sf * (1 - p.comm - p.fee) - p.pkg;
export const bucket = (l) =>
  l <= 3
    ? "0-3"
    : l <= 7
      ? "4-7"
      : l <= 14
        ? "8-14"
        : l <= 30
          ? "15-30"
          : l <= 60
            ? "31-60"
            : "61+";

export const addDays = (s, k) =>
  new Date(Date.parse(s + "T12:00:00Z") + k * 864e5).toISOString().slice(0, 10);

export const TYPES = [
  "Last-minute",
  "Member-only",
  "Early booker",
  "Value-add",
];

export const VERDICT_RULE = {
  "Pre-launch": "V0",
  Paused: "V0",
  "Too early": "V1",
  Scale: "V2",
  Keep: "V3",
  Fix: "V4",
  Stop: "V5",
};

export const VERDICT_NEXT = {
  "Pre-launch": "Clear leak nights before launch",
  Paused: "No action",
  "Too early": "Keep running; re-check when the sample is large enough",
  Scale: "Extend dates or widen the audience",
  Keep: "Keep running as is",
  Fix: "Reduce the discount or tighten fences",
  Stop: "End the promotion",
};

export const LEAK_NAME = {
  L1: "Blackout night",
  L2: "Below hurdle",
  L3: "Stacking",
};

export const LEAK_META = {
  L1: ["BLACKOUT_DATE", "Night is a Yield Radar blackout (P1)"],
  L2: ["BELOW_HURDLE", "promo_net < hurdle"],
  L3: [
    "STACKING",
    "Non-stackable discount stacks with Genius and combined net < hurdle",
  ],
  L4: ["PARITY_RISK", "Public OTA discount (not fenced)"],
};

export const NAMEKEYS = [
  ["lastMinute", "Last-minute"],
  ["memberOnly", "Member-only"],
  ["earlyBooker", "Early booker"],
  ["valueAdd", "Value-add"],
];

export const CHIP = {
  sug: ["sug", "Suggestions"],
  leak: ["leak", "Leaks"],
  perf: ["perf", "Performance"],
};

export const SECTIONS = [
  {
    id: "nights",
    title: "Night rules",
    desc: "How each future night is compared with last year and when it is blacked out.",
  },
  {
    id: "discounts",
    title: "Suggested discounts",
    desc: "How big, and what type, the new-campaign suggestion is.",
  },
  {
    id: "hurdle",
    title: "Hurdle tiers",
    desc: "The lowest net rate worth taking. The fuller a night is forecast to be, the higher the floor.",
  },
  {
    id: "channels",
    title: "Channels",
    desc: "The booking channels, their commission, which one is your direct channel, and the fallback owner.",
  },
  {
    id: "names",
    title: "Campaign names",
    desc: "The name shown for a suggested campaign, by channel and type.",
  },
  {
    id: "verdicts",
    title: "Verdict rules",
    desc: "When a promotion is Scale, Keep, Fix, Stop or Too early.",
  },
  {
    id: "health",
    title: "Health score",
    desc: "How the 100-point score is split. The four weights must add up to 100.",
  },
  {
    id: "leaks",
    title: "Leak rules",
    desc: "The four ways a promotion can lose money. The logic is fixed; the wording of the action is editable for the RM team.",
  },
];

export const SETTINGS_META = [
  {
    id: "PaceAhead",
    group: "nights",
    label: "Pace ahead at",
    type: "num",
    unit: "index",
    min: 0.1,
    max: 3,
    step: 0.01,
    usedBy: ["sug"],
    meaning:
      'A night is "ahead of last year" when its pace index (rooms booked now ÷ rooms booked last year) is at or above this.',
  },
  {
    id: "AdrAhead",
    group: "nights",
    label: "ADR ahead at",
    type: "num",
    unit: "index",
    min: 0.1,
    max: 3,
    step: 0.01,
    usedBy: ["sug"],
    meaning:
      "Same idea for price: ADR index (average price paid now ÷ last year) at or above this counts as ahead.",
  },
  {
    id: "HealthyTightenFcst",
    group: "nights",
    label: "Healthy blackout from",
    type: "pct",
    unit: "%",
    min: 0,
    max: 1,
    step: 1,
    usedBy: ["sug", "leak"],
    meaning:
      "A Healthy night forecast to be this full or more is a blackout (P1): no discounts on it.",
  },
  {
    id: "AnyTightenFcst",
    group: "nights",
    label: "Any-night blackout from",
    type: "pct",
    unit: "%",
    min: 0,
    max: 1,
    step: 1,
    usedBy: ["sug", "leak"],
    meaning:
      "Any night forecast to be this full or more is a blackout, whatever its label.",
  },
  {
    id: "PromoDesired",
    group: "discounts",
    label: "Desired discount",
    type: "pct",
    unit: "%",
    min: 0,
    max: 0.9,
    step: 1,
    usedBy: ["sug"],
    meaning: "The normal discount to suggest on a soft night.",
  },
  {
    id: "PromoDeep",
    group: "discounts",
    label: "Deep discount",
    type: "pct",
    unit: "%",
    min: 0,
    max: 0.9,
    step: 1,
    usedBy: ["sug"],
    meaning:
      "A bigger discount, used when a night is badly behind last year (pace below the trigger).",
  },
  {
    id: "PromoDeepPace",
    group: "discounts",
    label: "Deep discount trigger",
    type: "num",
    unit: "index",
    min: 0,
    max: 3,
    step: 0.01,
    usedBy: ["sug"],
    meaning: "Use the deep discount when the night's pace index is below this.",
  },
  {
    id: "PromoMinDisc",
    group: "discounts",
    label: "Minimum discount",
    type: "pct",
    unit: "%",
    min: 0,
    max: 0.5,
    step: 1,
    usedBy: ["sug", "leak"],
    meaning:
      "If the rounded discount is below this, suggest a value-add (e.g. breakfast) instead of a price cut.",
  },
  {
    id: "CapStep",
    group: "discounts",
    label: "Discount cap step",
    type: "pct",
    unit: "%",
    min: 0.005,
    max: 0.5,
    step: 0.5,
    usedBy: ["sug", "leak"],
    meaning:
      "Discounts round down to a multiple of this step (5% gives 5, 10, 15 …). Used for capped discounts in leak fixes and for suggested campaign discounts.",
  },
  {
    id: "PromoLastMinDays",
    group: "discounts",
    label: "Last-minute window",
    type: "int",
    unit: "days",
    min: 0,
    max: 365,
    usedBy: ["sug"],
    meaning: "Nights this many days out or fewer get a last-minute offer.",
  },
  {
    id: "PromoMemberDays",
    group: "discounts",
    label: "Member-only window",
    type: "int",
    unit: "days",
    min: 1,
    max: 730,
    usedBy: ["sug"],
    meaning:
      "Nights further out than last-minute, up to this many days, get a member-only offer. Beyond it: early booker.",
  },
  {
    id: "EarlyBookLead",
    group: "discounts",
    label: "Early-booker lead",
    type: "int",
    unit: "days",
    min: 0,
    max: 730,
    usedBy: [],
    notUsed: true,
    meaning:
      "Early-booker offers close this many days before arrival. No formula reads this yet.",
  },
  {
    id: "DefaultChannel",
    group: "channels",
    label: "Fallback channel",
    type: "channel",
    unit: "",
    usedBy: ["sug"],
    isNew: true,
    meaning:
      "The channel that gets the suggestion when no channel is behind last year.",
  },
  {
    id: "MinSample",
    group: "verdicts",
    label: "Min room nights for a verdict",
    type: "int",
    unit: "nights",
    min: 1,
    max: 100000,
    usedBy: ["perf"],
    meaning: 'Fewer room nights than this gives "Too early".',
  },
  {
    id: "MinDaysLive",
    group: "verdicts",
    label: "Min days live for a verdict",
    type: "int",
    unit: "days",
    min: 1,
    max: 3650,
    usedBy: ["perf"],
    meaning: 'Live for fewer days than this gives "Too early".',
  },
  {
    id: "ScaleROI",
    group: "verdicts",
    label: "Scale: min ROI",
    type: "num",
    unit: "x",
    min: 0,
    max: 100,
    step: 0.1,
    usedBy: ["perf"],
    meaning: 'ROI at or above this (and enough new demand) gives "Scale".',
  },
  {
    id: "ScaleIncr",
    group: "verdicts",
    label: "Scale: min incrementality",
    type: "pct",
    unit: "%",
    min: 0,
    max: 1,
    step: 1,
    usedBy: ["perf"],
    meaning: 'Share of bookings that must be new demand to qualify for "Scale".',
  },
  {
    id: "FixIncr",
    group: "verdicts",
    label: "Fix: min incrementality",
    type: "pct",
    unit: "%",
    min: 0,
    max: 1,
    step: 1,
    usedBy: ["perf"],
    meaning:
      'A promotion losing money is "Fix" if at least this share of bookings is new demand, otherwise "Stop".',
  },
  {
    id: "H_ROI",
    group: "health",
    label: "Weight: ROI",
    type: "int",
    unit: "points",
    min: 0,
    max: 100,
    usedBy: ["perf"],
    meaning: "Points of the 100-point health score that come from ROI.",
  },
  {
    id: "H_Incr",
    group: "health",
    label: "Weight: incrementality",
    type: "int",
    unit: "points",
    min: 0,
    max: 100,
    usedBy: ["perf"],
    meaning: "Points that come from the share of new demand.",
  },
  {
    id: "H_Leak",
    group: "health",
    label: "Weight: leak-free",
    type: "int",
    unit: "points",
    min: 0,
    max: 100,
    usedBy: ["perf"],
    meaning: "Points that come from nights without a leak.",
  },
  {
    id: "H_Target",
    group: "health",
    label: "Weight: target",
    type: "int",
    unit: "points",
    min: 0,
    max: 100,
    usedBy: ["perf"],
    meaning: "Points that come from reaching the room-night target.",
  },
];

export const VERDICT_META = [
  { verdict: "Pre-launch", rule: "V0", when: "Promotion status is Planned" },
  { verdict: "Paused", rule: "V0", when: "Promotion status is Paused" },
  {
    verdict: "Too early",
    rule: "V1",
    when: "Fewer room nights, or fewer days live, than the minimums above",
  },
  {
    verdict: "Scale",
    rule: "V2",
    when: "ROI and incrementality both reach the Scale minimums",
  },
  { verdict: "Keep", rule: "V3", when: "ROI is 0 or more" },
  {
    verdict: "Fix",
    rule: "V4",
    when: "ROI is below 0 and incrementality reaches the Fix minimum",
  },
  { verdict: "Stop", rule: "V5", when: "Anything else" },
];

/* ===== MATCH RESERVATIONS ===== */
export function matchReservations(d) {
  const alias = d.channelAliases || {};
  const up = {};
  Object.keys(alias).forEach((k) => (up[String(k).toUpperCase()] = alias[k]));
  const byKey = {};
  (d.promotions || []).forEach((p) =>
    (p.ratePlanCodes || []).forEach((c) => {
      const k = p.channel + "|" + String(c).trim().toUpperCase();
      (byKey[k] = byKey[k] || []).push(p);
    }),
  );
  const out = [];
  let total = 0;
  let matched = 0;

  (d.reservations || []).forEach((r) => {
    total++;
    const ch = up[String(r.channel).toUpperCase()] || r.channel;
    const cands =
      byKey[ch + "|" + String(r.rate_plan_code || "").trim().toUpperCase()];
    if (!cands || !r.arrival_date) return;
    const nights = r.departure_date
      ? Math.max(
          1,
          Math.round(
            (Date.parse(r.departure_date) - Date.parse(r.arrival_date)) / 864e5,
          ),
        )
      : 1;
    const status =
      r.cancel_date || /cancel|no.?show/i.test(r.status || "")
        ? "Cancelled"
        : "Confirmed";
    let hit = false;
    for (let i = 0; i < nights; i++) {
      const night = addDays(r.arrival_date, i);
      const p = cands.find((q) => night >= q.stayStart && night <= q.stayEnd);
      if (p) {
        hit = true;
        out.push([
          String(r.confirmation_no),
          p.id,
          ch,
          night,
          r.booking_date,
          r.room_type || "",
          r.nightly_rate,
          status,
        ]);
      }
    }
    if (hit) matched++;
  });
  return { bookings: out, resInfo: { total, matched } };
}

/* ===== ALIGN DATA TO HORIZON ===== */
export function buildAlignedData(rawSample, targetAsOf, sDate, eDate, hotelOverrides = {}, zeroValues = true) {
  const baseAsOf = (rawSample && rawSample.hotel && rawSample.hotel.asOf) || "2026-10-03";
  const asOfStr = targetAsOf && moment(targetAsOf).isValid()
    ? moment(targetAsOf).format("YYYY-MM-DD")
    : baseAsOf;

  const mTarget = moment(asOfStr);
  const mBase = moment(baseAsOf);
  const dayShift = Math.round((mTarget.valueOf() - mBase.valueOf()) / 864e5);

  const shiftDateStr = (dStr) => {
    if (!dStr || typeof dStr !== "string") return dStr;
    const m = moment(dStr);
    if (!m.isValid()) return dStr;
    return m.add(dayShift, "days").format("YYYY-MM-DD");
  };

  const startMoment = sDate && moment(sDate).isValid()
    ? moment(sDate).startOf("day")
    : moment(asOfStr).startOf("day");

  const endMoment = eDate && moment(eDate).isValid()
    ? moment(eDate).endOf("day")
    : moment(startMoment).add(2, "months").endOf("day");

  const sampleDaily = (rawSample && Array.isArray(rawSample.daily)) ? rawSample.daily : [];
  const alignedDaily = [];

  const cur = moment(startMoment);
  let idx = 0;
  while (cur.isSameOrBefore(endMoment, "day")) {
    const curStr = cur.format("YYYY-MM-DD");
    const template = sampleDaily.length > 0
      ? sampleDaily[idx % sampleDaily.length]
      : {
          otbRooms: 0,
          otbRevenue: 0,
          stlyRooms: 0,
          stlyRevenue: 0,
          forecastRooms: 0,
          bar: 0,
          channels: {},
        };

    alignedDaily.push({
      ...(zeroValues ? {} : template),
      date: curStr,
      event: zeroValues ? "" : (template.event || ""),
      otbRooms: zeroValues ? 0 : (template.otbRooms || 0),
      otbRevenue: zeroValues ? 0 : (template.otbRevenue || 0),
      stlyRooms: zeroValues ? 0 : (template.stlyRooms || 0),
      stlyRevenue: zeroValues ? 0 : (template.stlyRevenue || 0),
      forecastRooms: zeroValues ? 0 : (template.forecastRooms || 0),
      bar: zeroValues ? 0 : (template.bar || 0),
      channels: (() => {
        const baseCh = {
          "BOOKING.COM": { ty: 0, stly: 0 },
          "BRAND": { ty: 0, stly: 0 },
          "DIRECT": { ty: 0, stly: 0 },
          "EXPEDIA": { ty: 0, stly: 0 },
          "UNMAPPED": { ty: 0, stly: 0 },
        };
        if (!zeroValues && template.channels) {
          Object.entries(template.channels).forEach(([k, v]) => {
            const ck = cleanChannelName(k);
            if (ck) baseCh[ck] = v;
          });
        }
        return baseCh;
      })(),
      adr: zeroValues ? 0 : (template.adr != null ? template.adr : 0),
      leftToSell: zeroValues ? 0 : (template.leftToSell != null ? template.leftToSell : 0),
      OnTheBook: zeroValues ? 0 : (template.OnTheBook != null ? template.OnTheBook : 0),
      stlyotb: zeroValues ? 0 : (template.stlyotb != null ? template.stlyotb : 0),
      RMS: zeroValues ? 0 : (template.RMS != null ? template.RMS : 0),
      Rate: zeroValues ? 0 : (template.Rate != null ? template.Rate : 0),
      ADR: zeroValues ? 0 : (template.ADR != null ? template.ADR : 0),
      LeftToSell: zeroValues ? 0 : (template.LeftToSell != null ? template.LeftToSell : 0),
    });

    cur.add(1, "days");
    idx++;
  }

  const alignedPromotions = (rawSample && Array.isArray(rawSample.promotions) ? rawSample.promotions : []).map((p) => ({
    ...p,
    channel: cleanChannelName(p.channel),
    stayStart: shiftDateStr(p.stayStart),
    stayEnd: shiftDateStr(p.stayEnd),
    bookStart: shiftDateStr(p.bookStart),
    bookEnd: shiftDateStr(p.bookEnd),
    launchDate: shiftDateStr(p.launchDate),
  }));

  const alignedBookings = (rawSample && Array.isArray(rawSample.bookings) ? rawSample.bookings : []).map((b) => ({
    ...b,
    channel: cleanChannelName(b.channel),
    stayDate: shiftDateStr(b.stayDate),
    bookedDate: shiftDateStr(b.bookedDate),
  }));

  const alignedCampaigns = (rawSample && Array.isArray(rawSample.campaigns) ? rawSample.campaigns : []).map((c) => ({
    ...c,
    stayStart: shiftDateStr(c.stayStart),
    stayEnd: shiftDateStr(c.stayEnd),
    bookStart: shiftDateStr(c.bookStart),
    bookEnd: shiftDateStr(c.bookEnd),
  }));

  const hotel = {
    name: hotelOverrides.name || (rawSample && rawSample.hotel && rawSample.hotel.name) || "Hotel",
    capacity: hotelOverrides.capacity || (rawSample && rawSample.hotel && rawSample.hotel.capacity) || 150,
    asOf: asOfStr,
  };

  return {
    hotel,
    daily: alignedDaily,
    promotions: alignedPromotions,
    campaigns: alignedCampaigns,
    bookings: alignedBookings,
    channelAliases: rawSample.channelAliases || {},
  };
}

/* ===== INGEST RAW DATA ===== */
export function ingestData(d) {
  if (
    !d ||
    !d.hotel ||
    !Array.isArray(d.daily) ||
    !d.daily.length ||
    !Array.isArray(d.promotions) ||
    !(Array.isArray(d.bookings) || Array.isArray(d.reservations))
  ) {
    throw new Error(
      "The data needs hotel, daily (at least one night), promotions, and bookings or reservations.",
    );
  }
  let cleanAsOf = d.hotel && d.hotel.asOf ? String(d.hotel.asOf) : "";
  if (cleanAsOf && !/^\d{4}-\d{2}-\d{2}$/.test(cleanAsOf)) {
    try {
      const parsed = new Date(cleanAsOf);
      if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear();
        const m = String(parsed.getMonth() + 1).padStart(2, "0");
        const dt = String(parsed.getDate()).padStart(2, "0");
        cleanAsOf = `${y}-${m}-${dt}`;
      }
    } catch {
      cleanAsOf = "2026-10-03";
    }
  }
  if (!cleanAsOf || !/^\d{4}-\d{2}-\d{2}$/.test(cleanAsOf)) {
    cleanAsOf = "2026-10-03";
  }

  const rawCap = d.hotel ? +d.hotel.capacity : 0;
  const cleanCapacity = isNum(rawCap) && rawCap > 0 ? rawCap : 150;

  const hotelInfo = {
    name: String((d.hotel && d.hotel.name) || "Hotel"),
    capacity: cleanCapacity,
    asOf: cleanAsOf,
  };

  const daily = d.daily.map((r) => [
    r.date,
    r.event || "",
    r.otbRooms,
    r.otbRevenue,
    r.stlyRooms,
    r.stlyRevenue,
    r.forecastRooms,
    r.bar,
    r.channels || {},
    r.adr,
    r.leftToSell,
    r.OnTheBook,
    r.stlyotb,
    r.RMS,
    r.Rate,
    r.ADR,
    r.LeftToSell,
  ]);
  const promos = d.promotions.map((p) => [
    p.id,
    p.name,
    p.channel,
    p.type,
    p.discountPct,
    p.extraFeePct || 0,
    p.packageCost || 0,
    p.stayStart,
    p.stayEnd,
    p.bookStart,
    p.bookEnd,
    p.minLeadDays || 0,
    p.maxLeadDays == null ? 999 : p.maxLeadDays,
    p.stackable ? "Yes" : "No",
    p.fenced ? "Yes" : "No",
    p.status,
    p.launchDate,
    p.source || "",
    p.promoShare,
    p.targetRoomNights,
    p.ratePlanCodes || [],
  ]);

  let bookings = (d.bookings || []).map((b) => [
    b.confirmation,
    b.promotionId,
    b.channel,
    b.stayDate,
    b.bookedDate,
    b.roomType,
    b.rate,
    b.status,
  ]);
  let resInfo = { total: 0, matched: 0 };
  if (Array.isArray(d.reservations)) {
    const resRes = matchReservations(d);
    bookings = bookings.concat(resRes.bookings);
    resInfo = resRes.resInfo;
  }

  const dataKeys = new Set();
  daily.forEach((r) => Object.keys(r[8] || {}).forEach((k) => dataKeys.add(k)));

  return { hotelInfo, daily, promos, bookings, resInfo, dataKeys };
}

/* ===== CONFIG HYDRATION & DEHYDRATION ===== */
export function hydrateConfig(raw) {
  const dflt = sampleConfig;
  const cfg = raw?.settings || {};
  const st = {
    settings: SETTINGS_META.map((m) =>
      Object.assign({}, m, {
        value: cfg[m.id] != null ? cfg[m.id] : dflt.settings[m.id],
      }),
    ),
    hurdleTiers: [],
    channels: [],
    names: {},
    verdicts: VERDICT_META.map((m) =>
      Object.assign({}, m, {
        nextStep:
          ((raw?.verdicts || []).find((v) => v.verdict === m.verdict) || {})
            .nextStep || "",
      }),
    ),
    leakRules: ["L1", "L2", "L3", "L4"].map((c) => ({
      code: c,
      name: LEAK_META[c][0],
      condition: LEAK_META[c][1],
      action:
        ((raw?.leakRules || []).find((l) => l.code === c) || {}).action || "",
    })),
  };

  st.hurdleTiers = (raw?.hurdleTiers || []).map((r, idx) =>
    Object.assign(
      { _id: r._id || `ht_${idx}_${r.minForecast}` },
      { minForecast: r.minForecast, hurdlePct: r.hurdlePct },
    ),
  );
  st.channels = (raw?.channels || []).map((r, idx) => ({
    _id: r._id || `ch_${idx}_${r.name}`,
    name: r.name,
    commission: r.commission,
    direct: !!r.direct,
    dataKey: r.dataKey || null,
  }));
  st.channels.forEach((c) => {
    const n =
      (raw?.promotionNames || []).find((p) => p.owner === c.name) || {};
    st.names[c._id] = {
      lastMinute: n.lastMinute || "",
      memberOnly: n.memberOnly || "",
      earlyBooker: n.earlyBooker || "",
      valueAdd: n.valueAdd || "",
    };
  });
  const dc = st.settings.find((s) => s.id === "DefaultChannel");
  if (dc) {
    const directCh = st.channels.find((c) => c.direct);
    const m = st.channels.find((c) => c.name === dc.value);
    dc.value = m ? m._id : directCh ? directCh._id : st.channels[0] ? st.channels[0]._id : null;
  }
  return st;
}

export function dehydrateConfig(st) {
  const chName = (id) => {
    const c = st.channels.find((x) => x._id === id);
    return c ? c.name : "";
  };
  const settings = {};
  st.settings.forEach((s) => {
    settings[s.id] = s.type === "channel" ? chName(s.value) : s.value;
  });
  return {
    settings,
    hurdleTiers: st.hurdleTiers.map((r) => ({
      minForecast: r.minForecast,
      hurdlePct: r.hurdlePct,
    })),
    channels: st.channels.map((c) => ({
      name: c.name,
      dataKey: c.dataKey || c.name,
      commission: c.commission,
      direct: !!c.direct,
    })),
    promotionNames: st.channels.map((c) =>
      Object.assign({ owner: c.name }, st.names[c._id]),
    ),
    verdicts: st.verdicts.map((v) => ({
      verdict: v.verdict,
      nextStep: v.nextStep,
    })),
    leakRules: st.leakRules.map((l) => ({ code: l.code, action: l.action })),
  };
}

export function alignIds(st, ref) {
  st.hurdleTiers.forEach((r, i) => {
    if (ref.hurdleTiers[i]) r._id = ref.hurdleTiers[i]._id;
  });
  const names = {};
  const used = new Set();
  st.channels.forEach((c, i) => {
    const m =
      ref.channels.find((x) => x.name === c.name && !used.has(x._id)) ||
      (ref.channels[i] && !used.has(ref.channels[i]._id)
        ? ref.channels[i]
        : null);
    const old = c._id;
    if (m) {
      c._id = m._id;
      used.add(m._id);
    }
    names[c._id] = st.names[old];
  });
  st.names = names;
}

export function cloneObj(o) {
  return JSON.parse(JSON.stringify(o));
}

export function sameObj(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function diffAll(a, b) {
  const out = [];
  const add = (section, label, from, to) =>
    out.push({ section, label, from, to });
  b.settings.forEach((s, i) => {
    const o = a.settings[i];
    if (!sameObj(o.value, s.value)) {
      add(
        s.group,
        s.label,
        fmtVal(a, o, o.value),
        fmtVal(b, s, s.value),
      );
    }
  });

  const tableDiff = (section, ra, rb, rowName, fields) => {
    rb.forEach((r) => {
      const o = ra.find((x) => x._id === r._id);
      if (!o) {
        add(section, rowName(r) + " (new row)", "—", "added");
        return;
      }
      fields.forEach((f) => {
        if (!sameObj(o[f.k], r[f.k])) {
          add(section, rowName(o) + ": " + f.label, f.fmt(o[f.k]), f.fmt(r[f.k]));
        }
      });
    });
    ra.forEach((o) => {
      if (!rb.find((x) => x._id === o._id)) {
        add(section, rowName(o) + " (removed)", "removed", "—");
      }
    });
  };

  tableDiff(
    "hurdle",
    a.hurdleTiers,
    b.hurdleTiers,
    (r) => "Tier from " + pctTxt(r.minForecast),
    [
      { k: "minForecast", label: "starts at", fmt: pctTxt },
      { k: "hurdlePct", label: "hurdle", fmt: pctTxt },
    ],
  );
  tableDiff(
    "channels",
    a.channels,
    b.channels,
    (r) => "Channel " + (r.name || "(unnamed)"),
    [
      { k: "name", label: "name", fmt: (v) => v || "—" },
      { k: "commission", label: "commission", fmt: pctTxt },
      {
        k: "direct",
        label: "direct channel",
        fmt: (v) => (v ? "Yes" : "No"),
      },
    ],
  );
  const ia = a.channels.map((c) => c._id);
  const ib = b.channels.map((c) => c._id);
  if (!sameObj(ia, ib) && sameObj([...ia].sort(), [...ib].sort())) {
    add(
      "channels",
      "Channel order",
      a.channels.map((c) => c.name).join(", "),
      b.channels.map((c) => c.name).join(", "),
    );
  }
  b.channels.forEach((c) => {
    const o = a.names[c._id];
    if (!o) return;
    NAMEKEYS.forEach(([k, lab]) => {
      if (o[k] !== b.names[c._id][k]) {
        add(
          "names",
          c.name + " · " + lab,
          o[k] || "—",
          b.names[c._id][k] || "—",
        );
      }
    });
  });
  b.verdicts.forEach((v, i) => {
    if (v.nextStep !== a.verdicts[i].nextStep) {
      add(
        "verdicts",
        v.verdict + ": next step",
        a.verdicts[i].nextStep,
        v.nextStep,
      );
    }
  });
  b.leakRules.forEach((l, i) => {
    if (l.action !== a.leakRules[i].action) {
      add("leaks", l.code + ": action", a.leakRules[i].action, l.action);
    }
  });
  return out;
}

export function fmtVal(st, s, v) {
  if (s.type === "channel") {
    const c = st.channels.find((x) => x._id === v);
    return c ? c.name : "—";
  }
  if (s.type === "date" || s.type === "text") return v || "—";
  if (v == null) return "—";
  if (s.type === "pct") return pctTxt(v);
  return v + (s.unit && s.unit !== "index" ? " " + s.unit : "");
}

/* ===== MAIN COMPUTATION ENGINE ===== */
export function computePromoRadar(dataPayload, configPayload) {
  const { hotelInfo, daily, promos, bookings, resInfo, dataKeys } =
    dataPayload;
  const p = configPayload;
  const sv = (id) => p.settings[id];

  const config = {
    asOf: hotelInfo.asOf,
    capacity: hotelInfo.capacity,
    paceAhead: sv("PaceAhead"),
    adrAhead: sv("AdrAhead"),
    healthyTightenFcst: sv("HealthyTightenFcst"),
    anyTightenFcst: sv("AnyTightenFcst"),
    promoDesired: sv("PromoDesired"),
    promoDeep: sv("PromoDeep"),
    promoDeepPace: sv("PromoDeepPace"),
    promoMinDisc: sv("PromoMinDisc"),
    lastMinDays: sv("PromoLastMinDays"),
    memberDays: sv("PromoMemberDays"),
    earlyBookLead: sv("EarlyBookLead"),
    minSample: sv("MinSample"),
    minDaysLive: sv("MinDaysLive"),
    scaleROI: sv("ScaleROI"),
    scaleIncr: sv("ScaleIncr"),
    fixIncr: sv("FixIncr"),
    hROI: sv("H_ROI"),
    hIncr: sv("H_Incr"),
    hLeak: sv("H_Leak"),
    hTarget: sv("H_Target"),
    capStep: sv("CapStep"),
  };

  config.hurdleTiers = (p.hurdleTiers || [])
    .map((r) => [r.minForecast, r.hurdlePct])
    .sort((a, b) => b[0] - a[0]);

  config.channels = Object.fromEntries(
    (p.channels || []).map((c) => [c.name, c.commission]),
  );

  const chan = (p.channels || []).map((c) => ({
    name: c.name,
    key: c.dataKey || c.name,
    commission: c.commission,
    direct: !!c.direct,
  }));
  const chNames = chan.map((c) => c.name);
  const directCh = (chan.find((c) => c.direct) || {}).name || null;
  const fallbackCh = chNames.includes(sv("DefaultChannel"))
    ? sv("DefaultChannel")
    : chNames[0] || "DIRECT";
  config.fallbackChannel = fallbackCh;

  const promoNames = {};
  (p.promotionNames || []).forEach((n) => {
    promoNames[n.owner] = {
      "Last-minute": n.lastMinute,
      "Member-only": n.memberOnly,
      "Early booker": n.earlyBooker,
      "Value-add": n.valueAdd,
    };
  });

  const verdictNext = {};
  (p.verdicts || []).forEach((v) => {
    verdictNext[v.verdict] = v.nextStep;
  });

  const leakRules = p.leakRules || [];

  const chColor = {};
  const baseColors = {
    "BOOKING.COM": "var(--booking)",
    "Booking.com": "var(--booking)",
    "BRAND": "var(--brand)",
    "Brand.com": "var(--brand)",
    "DIRECT": "var(--ch7, #10b981)",
    "Direct": "var(--ch7, #10b981)",
    "EXPEDIA": "var(--expedia)",
    "Expedia": "var(--expedia)",
    "UNMAPPED": "var(--ch4)",
    "GDS": "var(--ch5)",
    "Corporate": "var(--ch6)",
    "Wholesale": "var(--ch8)",
  };
  let extraCol = 0;
  chan.forEach((c) => {
    chColor[c.name] =
      baseColors[c.key] ||
      baseColors[c.name] ||
      "var(--ch" + (4 + (extraCol++ % 5)) + ")";
  });

  const asOfNum = dnum(config.asOf);

  // Compute NIGHTS
  const nights = daily.map((r) => {
    const C = config.capacity;
    const otb = r[2] / C;
    const stly = r[4] / C;
    const fc = Math.max(r[6], r[2]) / C;
    const adr = r[9] != null && r[9] > 0 ? r[9] : (r[2] ? r[3] / r[2] : 0);
    const sadr = r[4] ? r[5] / r[4] : 0;
    const pace = stly ? otb / stly : 1;
    const ai = sadr ? adr / sadr : 1;
    const diag =
      pace >= config.paceAhead
        ? ai >= config.adrAhead
          ? "Healthy"
          : "Underpriced"
        : ai >= config.adrAhead
          ? "Price resistance"
          : "Demand gap";

    const tierObj = config.hurdleTiers.find((t) => fc >= t[0]);
    const tier = tierObj ? tierObj[1] : 0.55;
    const hurdle = Math.round(r[7] * tier);
    const blackout =
      diag === "Underpriced" ||
      (diag === "Healthy" && fc >= config.healthyTightenFcst) ||
      fc >= config.anyTightenFcst;

    const ty = {};
    const ly = {};
    chan.forEach((c) => {
      const f = (r[8] || {})[c.key] || (r[8] || {})[c.name];
      ty[c.name] = f ? f.ty : 0;
      ly[c.name] = f ? f.stly : 0;
    });

    const share = Object.fromEntries(
      chNames.map((c) => [c, r[2] ? ty[c] / r[2] : 0]),
    );

    return {
      d: r[0],
      n: dnum(r[0]),
      ev: r[1],
      otbR: r[2],
      stlyR: r[4],
      fcR: r[6],
      bar: r[7],
      OnTheBook: r[11] != null ? r[11] : r[2],
      stlyotb: r[12] != null ? r[12] : r[4],
      RMS: r[13] != null ? r[13] : r[6],
      Rate: r[14] != null ? r[14] : r[7],
      ADR: r[15] != null ? r[15] : adr,
      otb,
      stly,
      fc,
      adr,
      sadr,
      pace,
      ai,
      diag,
      tier,
      hurdle,
      blackout,
      rem: r[10] != null ? r[10] : Math.max(0, r[6] - r[2]),
      leftToSell: r[10] != null ? r[10] : Math.max(0, r[6] - r[2]),
      LeftToSell: r[16] != null ? r[16] : (r[10] != null ? r[10] : Math.max(0, r[6] - r[2])),
      ty,
      ly,
      share,
      dba: dnum(r[0]) - asOfNum,
    };
  });

  const nidx = Object.fromEntries(nights.map((x, k) => [x.d, k]));
  const chOf = (name) =>
    chan.find(
      (c) =>
        c.key === name ||
        c.name === name ||
        c.key?.toLowerCase() === String(name || '').toLowerCase() ||
        c.name?.toLowerCase() === String(name || '').toLowerCase()
    );
  // DYNAMIC CONFIGURATION-DRIVEN PROMOTIONS:
  // Derive the promotion list directly from configPayload.promotionNames (from api/widget/get_widget_config)
  let configuredPromos = promos;
  if (Array.isArray(p.promotionNames) && p.promotionNames.length > 0) {
    let promoIdx = 101;
    const dynamicList = [];
    const minStayDate = (daily && daily[0] && daily[0][0]) || config.asOf;
    const maxStayDate = (daily && daily[daily.length - 1] && daily[daily.length - 1][0]) || config.asOf;
    const asOfMoment = moment(config.asOf);
    const bookStartStr = asOfMoment.clone().subtract(30, "days").format("YYYY-MM-DD");
    const bookEndStr = maxStayDate;

    const campaignTypesMeta = [
      { key: "memberOnly", type: "Member-only", disc: 0.10, minL: 0, maxL: 999, stack: "Yes", fenced: "Yes", status: "Live", share: 0.25, target: 100 },
      { key: "lastMinute", type: "Last-minute", disc: 0.15, minL: 0, maxL: sv("PromoLastMinDays") || 7, stack: "No", fenced: "No", status: "Live", share: 0.15, target: 40 },
      { key: "earlyBooker", type: "Early booker", disc: 0.20, minL: sv("EarlyBookLead") || 30, maxL: 999, stack: "No", fenced: "No", status: "Live", share: 0.15, target: 50 },
      { key: "valueAdd", type: "Value-add", disc: 0.00, minL: 0, maxL: 999, stack: "No", fenced: "Yes", status: "Planned", share: 0.10, target: 30 },
    ];

    const promoNamesMap = new Map();
    (p.promotionNames || []).forEach((pn) => {
      const owner = pn.owner || pn.channel || "";
      if (owner) promoNamesMap.set(owner.toLowerCase(), pn);
    });
    chan.forEach((c) => {
      if (!promoNamesMap.has(c.name.toLowerCase())) {
        promoNamesMap.set(c.name.toLowerCase(), {
          owner: c.name,
          memberOnly: `${c.name} Member Discount`,
          lastMinute: `${c.name} Last-Minute Deal`,
          earlyBooker: `${c.name} Early Booker Offer`,
          valueAdd: `${c.name} Package Promotion`,
        });
      }
    });
    Array.from(promoNamesMap.values()).forEach((pn) => {
      const channelOwner = pn.owner || pn.channel || "";
      const channelObj = chOf(channelOwner) || { name: channelOwner, commission: 0.18 };
      const chName = channelObj.name;

      campaignTypesMeta.forEach((ct) => {
        const configuredName = pn[ct.key];
        if (!configuredName || !String(configuredName).trim()) return;

        const id = `P-${promoIdx++}`;
        dynamicList.push([
          id, // p[0]: id
          String(configuredName).trim(), // p[1]: name from get_widget_config
          chName, // p[2]: channel
          ct.type, // p[3]: type
          ct.disc, // p[4]: discountPct
          0, // p[5]: fee
          0, // p[6]: pkg
          minStayDate, // p[7]: stayStart
          maxStayDate, // p[8]: stayEnd
          bookStartStr, // p[9]: bookStart
          bookEndStr, // p[10]: bookEnd
          ct.minL, // p[11]: minLeadDays
          ct.maxL, // p[12]: maxLeadDays
          ct.stack, // p[13]: stackable
          ct.fenced, // p[14]: fenced
          ct.status, // p[15]: status
          bookStartStr, // p[16]: launchDate
          "Configuration", // p[17]: source
          ct.share, // p[18]: promoShare
          ct.target, // p[19]: targetRoomNights
        ]);
      });
    });

    if (dynamicList.length > 0) {
      configuredPromos = dynamicList;
    }
  }

  const livePromos = configuredPromos.filter((p) => chOf(p[2]));
  const hiddenInfo = {
    promos: promos.length - livePromos.length,
    bookings: 0,
  };

  const pr = livePromos.map((p) => {
    const c = chOf(p[2]) || { name: p[2], commission: 0.18 };
    const ownerNames =
      promoNames[c.name] ||
      promoNames[p[2]] ||
      Object.entries(promoNames).find(
        ([k]) => k.toLowerCase() === String(c.name || p[2] || '').toLowerCase()
      )?.[1] ||
      {};
    const configuredName =
      ownerNames[p[3]] ||
      Object.entries(ownerNames).find(
        ([k]) => k.toLowerCase() === String(p[3] || '').toLowerCase()
      )?.[1];
    const resolvedName =
      configuredName && String(configuredName).trim()
        ? String(configuredName).trim()
        : p[1];
    return {
      id: p[0],
      name: resolvedName,
      ch: c.name,
      type: p[3],
      disc: p[4],
      fee: p[5],
      pkg: p[6],
      ss: dnum(p[7]),
      se: dnum(p[8]),
      bs: dnum(p[9]),
      be: dnum(p[10]),
      minL: p[11],
      maxL: p[12],
      stack: p[13] === "Yes",
      fenced: p[14] === "Yes",
      status: p[15],
      launch: dnum(p[16]),
      source: p[17],
      share: p[18],
      target: p[19],
      comm: c.commission,
      raw: p,
    };
  });

  const active = pr.map((p) =>
    nights.map((x) =>
      (p.status === "Live" || p.status === "Planned") &&
      x.n >= p.ss &&
      x.n <= p.se &&
      Math.min(p.be, x.n - p.minL) >= asOfNum
        ? 1
        : 0,
    ),
  );

  const sf = Object.fromEntries(
    chNames.map((c) => [
      c,
      nights.map((x, k) =>
        Math.exp(
          pr.reduce(
            (t, p, i) =>
              t +
              (p.ch === c && p.stack && active[i][k]
                ? Math.log(1 - p.disc)
                : 0),
            0,
          ),
        ),
      ),
    ]),
  );

  const code = pr.map((p, i) =>
    nights.map((x, k) => {
      if (!active[i][k]) return "";
      if (x.blackout) return "L1";
      const channelSf = (sf[p.ch] || [])[k] || 1;
      if (
        !p.stack &&
        p.disc > 0 &&
        channelSf < 0.9999 &&
        combNet(p, x.bar, channelSf) < x.hurdle
      ) {
        return "L3";
      }
      return promoNet(p, x.bar) < x.hurdle ? "L2" : "OK";
    }),
  );

  const leak = pr.map((p, i) =>
    nights.map((x, k) => {
      const c = code[i][k];
      if (!c || c === "OK") return 0;
      const channelSf = (sf[p.ch] || [])[k] || 1;
      const eff =
        c === "L3" ? combNet(p, x.bar, channelSf) : promoNet(p, x.bar);
      return r2(
        x.rem *
          (x.share[p.ch] || 0) *
          p.share *
          Math.max(0, x.bar * (1 - p.comm) - eff),
      );
    }),
  );

  const leaks = [];
  pr.forEach((p, i) =>
    nights.forEach((x, k) => {
      if (code[i][k] && code[i][k] !== "OK") {
        leaks.push({
          i,
          k,
          code: code[i][k],
          amt: leak[i][k],
          key: `${p.id}|${x.d}`,
        });
      }
    }),
  );
  leaks.sort(
    (a, b) => b.amt - a.amt || a.i * nights.length + a.k - (b.i * nights.length + b.k),
  );

  function getLeakAction(i, k) {
    const p = pr[i];
    const x = nights[k];
    const c = code[i][k];
    if (c === "L1") return "Exclude this night (blackout)";
    if (c === "L3") {
      const channelSf = (sf[p.ch] || [])[k] || 1;
      return `Turn off stacking with Genius (combined ${pct(1 - (1 - p.disc) * channelSf)})`;
    }
    if (c === "L2") {
      const cap =
        Math.floor(
          (Math.max(
            0,
            1 - (x.hurdle + p.pkg) / (x.bar * (1 - p.comm - p.fee)),
          ) +
            1e-6) /
            config.capStep,
        ) * config.capStep;
      return p.disc > 0 && cap >= config.promoMinDisc
        ? `Cap discount at ${pct(cap)}`
        : "Exclude this night";
    }
    return "";
  }

  pr.forEach((p, i) => {
    p.activeN = active[i].reduce((a, b) => a + b, 0);
    p.codes = { L1: 0, L2: 0, L3: 0 };
    code[i].forEach((c) => {
      if (p.codes[c] != null) p.codes[c]++;
    });
    p.leakN = p.codes.L1 + p.codes.L2 + p.codes.L3;
    p.leak$ = leak[i].reduce((a, b) => a + b, 0);
    p.parityN =
      directCh && !p.fenced && p.disc > 0 && p.ch !== directCh ? p.activeN : 0;
    p.leakAction =
      p.leakN === 0
        ? "No leak"
        : [
            p.codes.L1
              ? `Exclude ${p.codes.L1} blackout night${p.codes.L1 > 1 ? "s" : ""}.`
              : "",
            p.codes.L3
              ? `Fix stacking on ${p.codes.L3} night${p.codes.L3 > 1 ? "s" : ""}.`
              : "",
            p.codes.L2
              ? `Cap discount on ${p.codes.L2} night${p.codes.L2 > 1 ? "s" : ""}.`
              : "",
          ]
            .filter(Boolean)
            .join(" ");
  });

  const bookable = bookings.filter(
    (b) => nidx[b[3]] != null && pr.some((q) => q.id === b[1] || q.ch === b[2]),
  );
  hiddenInfo.bookings = bookings.length - bookable.length;

  const bk = bookable.map((b) => {
    const p = pr.find((q) => q.id === b[1]) || pr.find((q) => q.ch === b[2]) || pr[0];
    const x = nights[nidx[b[3]]];
    const o = {
      conf: b[0],
      promo: b[1],
      ch: p.ch,
      stay: b[3],
      booked: b[4],
      room: b[5],
      rate: b[6],
      status: b[7],
      lead: dnum(b[3]) - dnum(b[4]),
      bar: x.bar,
      disc: Math.max(0, x.bar - b[6]),
      comm: p.comm,
      feeP: p.fee,
      pkg: p.pkg,
      blackout: x.blackout,
    };
    o.feeAmt = o.rate * o.feeP;
    o.net = o.rate * (1 - o.comm - o.feeP) - o.pkg;
    o.full = o.bar * (1 - o.comm);
    return o;
  });

  const nightsIn = {};
  bk.forEach((b) => (nightsIn[b.conf] = (nightsIn[b.conf] || 0) + 1));
  const firstN = {};
  bk.forEach((b, j) => {
    if (!(b.conf in firstN)) firstN[b.conf] = j;
  });

  pr.forEach((p) => {
    const vis = p.type === "Visibility";
    const E = Math.max(p.ss, nights[0].n);
    const F = Math.min(p.se, nights[nights.length - 1].n);
    const win = nights.filter((x) => x.n >= E && x.n <= F);
    const conf = bk.filter((b) => b.promo === p.id && b.status === "Confirmed");
    const chTY = win.reduce((t, x) => t + (x.ty[p.ch] || 0), 0);
    const chLY = win.reduce((t, x) => t + (x.ly[p.ch] || 0), 0);
    const R = win.reduce((t, x) => t + x.otbR, 0);
    const S = win.reduce((t, x) => t + x.stlyR, 0);
    const G = vis ? chTY : conf.length;
    const H = vis
      ? win.reduce((t, x) => t + (x.ty[p.ch] || 0) * x.adr, 0)
      : conf.reduce((t, b) => t + b.rate, 0);
    const J = vis
      ? H * (1 - p.comm - p.fee)
      : conf.reduce((t, b) => t + b.net, 0);
    const Lf = vis
      ? H * (1 - p.comm)
      : conf.reduce((t, b) => t + b.full, 0);
    const M = vis ? 0 : conf.reduce((t, b) => t + b.disc, 0);
    const Nf = vis
      ? H * p.fee
      : conf.reduce((t, b) => t + b.feeAmt, 0);
    const O = M + Nf;
    const T = S - chLY ? (R - chTY) / (S - chLY) : 1;
    const U = chLY * T;
    const V = Math.max(0, chTY - U);
    const W = vis
      ? chTY
      : bk.filter(
          (b) =>
            b.ch === p.ch &&
            b.status === "Confirmed" &&
            dnum(b.stay) >= E &&
            dnum(b.stay) <= F,
        ).length;
    const X = G === 0 ? 0 : Math.min(G, (V * G) / Math.max(1, W));
    const Y = G ? X / G : 0;
    const Z = G - X;
    const K = G ? J / G : 0;
    const AA = vis ? X * (G ? Lf / G : 0) : X * K;
    const AB = vis ? Nf : Z * (G ? (Lf - J) / G : 0);
    const AC = AA - AB;
    const AD = O ? AC / O : 0;
    const AF = p.target ? (vis ? X : G) / p.target : 0;
    const AG = p.status === "Live" ? asOfNum - p.launch : 0;
    const leads = conf.map((b) => b.lead);
    const avgLead = leads.length
      ? leads.reduce((a, b) => a + b, 0) / leads.length
      : null;
    const verdict =
      p.status === "Planned"
        ? "Pre-launch"
        : p.status === "Paused"
          ? "Paused"
          : G < config.minSample || AG < config.minDaysLive
            ? "Too early"
            : AD >= config.scaleROI && Y >= config.scaleIncr
              ? "Scale"
              : AD >= 0
                ? "Keep"
                : Y >= config.fixIncr
                  ? "Fix"
                  : "Stop";
    const leakShare = p.activeN ? p.leakN / p.activeN : 0;
    const health =
      p.status === "Live"
        ? Math.round(
            (config.hROI * Math.min(1, Math.max(0, (AD + 1) / 2)) +
              config.hIncr * Y +
              config.hLeak * (1 - leakShare) +
              config.hTarget * Math.min(1, AF)) *
              10,
          ) / 10
        : null;

    p.perf = {
      vis,
      E,
      F,
      G,
      H,
      I: G ? H / G : 0,
      J,
      K,
      L: Lf,
      M,
      N: Nf,
      O,
      P: chTY,
      Q: chLY,
      R,
      S,
      T,
      U,
      V,
      W,
      X,
      Y,
      Z,
      AA,
      AB,
      AC,
      AD,
      AF,
      AG,
      avgLead,
      verdict,
      rule: VERDICT_RULE[verdict],
      leakShare,
      health,
    };
  });

  pr.forEach((p) => {
    p.perf.rank =
      p.perf.health == null
        ? null
        : pr.filter(
            (q) => q.perf.health != null && q.perf.health > p.perf.health,
          ).length + 1;
  });

  // Campaign recommendations
  nights.forEach((x) => {
    x.rec = x.blackout
      ? "Blackout"
      : x.diag === "Healthy"
        ? "No promotion"
        : "Promotion";
    if (x.rec !== "Promotion") return;
    const deltas = chNames.map((c) => (x.ty[c] || 0) - (x.ly[c] || 0));
    const mn = Math.min(...deltas);
    x.owner = mn >= 0 ? fallbackCh : chNames[deltas.indexOf(mn)];
    const comm = config.channels[x.owner] || 0.18;
    x.desired =
      x.diag === "Price resistance"
        ? 0
        : x.pace < config.promoDeepPace
          ? config.promoDeep
          : config.promoDesired;
    x.maxDisc = Math.max(0, 1 - x.hurdle / (x.bar * (1 - comm)));
    const step = config.capStep;
    const fl =
      Math.round(
        Math.floor((Math.min(x.desired, x.maxDisc) + 1e-6) / step) *
          step *
          1e6,
      ) / 1e6;
    x.ptype =
      x.diag === "Price resistance"
        ? "Value-add"
        : fl < config.promoMinDisc
          ? "Value-add"
          : x.dba <= config.lastMinDays
            ? "Last-minute"
            : x.dba <= config.memberDays
              ? "Member-only"
              : "Early booker";
    x.pdisc = x.ptype === "Value-add" ? 0 : fl;
    x.ptarget = Math.max(0, x.stlyR - x.otbR);
    x.pkey = x.owner + " | " + x.ptype;
  });

  const camps = [];
  chNames.forEach((o) =>
    TYPES.forEach((t) => {
      const key = o + " | " + t;
      const xs = nights.filter((x) => x.pkey === key);
      if (!xs.length) return;
      const cov = pr.find(
        (p) => p.status === "Live" && p.ch + " | " + p.type === key,
      );
      camps.push({
        owner: o,
        type: t,
        key,
        name: (promoNames[o] || {})[t] || "Unnamed campaign",
        nights: xs,
        disc: Math.min(...xs.map((x) => x.pdisc)),
        target: xs.reduce((s, x) => s + x.ptarget, 0),
        covered: cov ? cov.id : null,
      });
    }),
  );

  const live = pr.filter((p) => p.status === "Live");
  const atRisk = leaks.reduce((t, l) => t + l.amt, 0);
  const netImpact = live.reduce((t, p) => t + (p.perf?.AC || 0), 0);
  const newCampsCount = camps.filter((c) => !c.covered).length;

  return {
    hotelInfo,
    config,
    chan,
    chNames,
    directCh,
    fallbackCh,
    chColor,
    promoNames,
    verdictNext,
    leakRules,
    nights,
    nidx,
    pr,
    active,
    sf,
    code,
    leak,
    leaks,
    bk,
    nightsIn,
    firstN,
    camps,
    resInfo,
    hiddenInfo,
    getLeakAction,
    summary: {
      atRisk,
      leakNights: leaks.length,
      netImpact,
      newCamps: newCampsCount,
      noData: chan.filter((c) => !dataKeys.has(c.key)).map((c) => c.name),
      hiddenPromos: hiddenInfo.promos,
      hiddenBookings: hiddenInfo.bookings,
    },
  };
}


export function cleanChannelName(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  const lower = trimmed.toLowerCase();
  if (
    lower === "direct" ||
    lower === "direct web" ||
    lower === "website" ||
    lower === "web" ||
    lower === "direct_web" ||
    lower === "hotel direct"
  ) {
    return "DIRECT";
  }
  if (
    lower === "brand" ||
    lower === "brand.com" ||
    lower === "brand_com" ||
    lower === "crs" ||
    lower === "brand crs"
  ) {
    return "BRAND";
  }
  if (
    lower === "booking" ||
    lower === "booking.com" ||
    lower === "bcom" ||
    lower.startsWith("booking.com")
  ) {
    return "BOOKING.COM";
  }
  if (
    lower === "expedia" ||
    lower === "expedia.com" ||
    lower.startsWith("expedia")
  ) {
    return "EXPEDIA";
  }
  if (
    lower === "unmapped" ||
    lower === "un-mapped" ||
    lower === "unmapped channel"
  ) {
    return "UNMAPPED";
  }
  return trimmed;
}

export function mergeChannelsWithConfig(cfg, channelNamesList = []) {
  const base = cfg && typeof cfg === "object" ? { ...cfg } : { ...sampleConfig };
  const existingChannels = Array.isArray(base.channels) ? [...base.channels] : [];
  const existingMap = new Map();
  existingChannels.forEach((c) => {
    if (c?.name) existingMap.set(c.name.toLowerCase(), c);
  });

  const allNames = [];
  const seen = new Set();

  // 1. Dynamic API Channels (from channel_stly and reservation_data)
  (channelNamesList || []).forEach((raw) => {
    const nm = cleanChannelName(raw);
    if (nm && !seen.has(nm.toLowerCase()) && nm.toLowerCase() !== "agoda") {
      seen.add(nm.toLowerCase());
      allNames.push(nm);
    }
  });

  // 2. Existing user-configured channels from saved configuration (excluding sampleConfig defaults)
  existingChannels.forEach((c) => {
    const nm = cleanChannelName(c?.name);
    if (
      nm &&
      !seen.has(nm.toLowerCase()) &&
      nm.toLowerCase() !== "agoda" &&
      nm.toLowerCase() !== "brand.com"
    ) {
      seen.add(nm.toLowerCase());
      allNames.push(nm);
    }
  });

  // 3. Fallback only if no API data or config has arrived yet
  if (allNames.length === 0) {
    const fallbackChannels = ["BOOKING.COM", "BRAND", "DIRECT", "EXPEDIA", "UNMAPPED"];
    fallbackChannels.forEach((core) => {
      if (!seen.has(core.toLowerCase())) {
        seen.add(core.toLowerCase());
        allNames.push(core);
      }
    });
  }

  const filteredNames = allNames.filter((n) => n.toLowerCase() !== "agoda" && n.toLowerCase() !== "brand.com");

  const mergedChannels = filteredNames.map((chName, idx) => {
    const existing =
      existingMap.get(chName.toLowerCase()) ||
      (chName === "BRAND" ? existingMap.get("brand.com") : null) ||
      (chName === "BOOKING.COM" ? existingMap.get("booking.com") : null) ||
      (chName === "DIRECT" ? existingMap.get("direct") : null) ||
      (chName === "EXPEDIA" ? existingMap.get("expedia") : null);

    const isDirect = chName.toLowerCase() === "direct";

    if (existing) {
      return {
        ...existing,
        _id: existing._id || `ch_${idx}_${chName}`,
        name: chName,
        dataKey: chName,
        commission:
          typeof existing.commission === "number"
            ? existing.commission
            : isDirect
              ? 0
              : 0.18,
        direct: existing.direct !== undefined ? !!existing.direct : isDirect,
      };
    }

    return {
      _id: `ch_${idx}_${chName}`,
      name: chName,
      dataKey: chName,
      commission: isDirect ? 0 : 0.18,
      direct: isDirect,
    };
  });

  if (mergedChannels.length > 0 && !mergedChannels.some((c) => c.direct)) {
    const directIdx = mergedChannels.findIndex((c) => c.name.toLowerCase() === "direct");
    if (directIdx >= 0) {
      mergedChannels[directIdx].direct = true;
      mergedChannels[directIdx].commission = 0;
    } else {
      const brandIdx = mergedChannels.findIndex((c) => c.name.toLowerCase() === "brand");
      if (brandIdx >= 0) {
        mergedChannels[brandIdx].direct = true;
        mergedChannels[brandIdx].commission = 0;
      } else {
        mergedChannels[0].direct = true;
        mergedChannels[0].commission = 0;
      }
    }
  }

  const directChannelName =
    mergedChannels.find((c) => c.direct)?.name ||
    mergedChannels.find((c) => c.name === "DIRECT")?.name ||
    "DIRECT";

  const currentFallback = cleanChannelName(base.settings?.DefaultChannel);
  const fallbackCh = mergedChannels.some((c) => c.name === currentFallback)
    ? currentFallback
    : directChannelName;

  const existingPromoNames = Array.isArray(base.promotionNames)
    ? [...base.promotionNames]
    : [];
  const promoMap = new Map();
  existingPromoNames.forEach((pn) => {
    const owner = cleanChannelName(pn.owner || pn.channel);
    if (owner) promoMap.set(owner.toLowerCase(), pn);
  });

  const defaultCampaignLabels = {
    "BOOKING.COM": {
      memberOnly: "Booking.com Genius member discount",
      lastMinute: "Booking.com Last-minute deal",
      earlyBooker: "Booking.com Early booker deal",
      valueAdd: "Booking.com Visibility boost with breakfast-included rate",
    },
    "BRAND": {
      memberOnly: "Brand Loyalty member rate",
      lastMinute: "Brand Last-minute offer to email and app audience",
      earlyBooker: "Brand Advance purchase offer",
      valueAdd: "Brand Stay longer and save, breakfast included",
    },
    "DIRECT": {
      memberOnly: "Direct Book loyalty member rate",
      lastMinute: "Direct Last-minute web offer",
      earlyBooker: "Direct Advance purchase deal",
      valueAdd: "Direct Stay longer and save package",
    },
    "EXPEDIA": {
      memberOnly: "Expedia Member Only Deal",
      lastMinute: "Expedia Same-day and last-minute deal",
      earlyBooker: "Expedia Early booking discount",
      valueAdd: "Expedia Visibility boost with package rate",
    },
    "UNMAPPED": {
      memberOnly: "Channel Member discount",
      lastMinute: "Channel Last-minute offer",
      earlyBooker: "Channel Early booker deal",
      valueAdd: "Channel Visibility boost package",
    },
  };

  const mergedPromoNames = mergedChannels.map((c) => {
    const existingP = promoMap.get(c.name.toLowerCase());
    const defaults = defaultCampaignLabels[c.name] || {
      memberOnly: `${c.name} Member Discount`,
      lastMinute: `${c.name} Last-Minute Deal`,
      earlyBooker: `${c.name} Early Booker Offer`,
      valueAdd: `${c.name} Package Promotion`,
    };
    if (existingP) {
      return {
        ...defaults,
        ...existingP,
        owner: c.name,
      };
    }
    return {
      owner: c.name,
      ...defaults,
    };
  });

  return {
    ...base,
    settings: {
      ...(base.settings || {}),
      DefaultChannel: fallbackCh,
    },
    channels: mergedChannels,
    promotionNames: mergedPromoNames,
  };
}
